import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, PlayCircle, Sparkles } from "lucide-react";
import { calendlyUrl, calendlyLinkProps } from "../../lib/calendly";

const HERO_IMG = `${process.env.PUBLIC_URL}/assets/home-hero.png`;

// Stat bar — verified operational numbers
const stats = [
  {
    value: "80%+",
    label: "Candidate quality hit rate, reported by long-standing clients",
    verified: true,
  },
  { value: "5+", label: "Years in operation", verified: true },
  { value: "60+", label: "Clients served", verified: true },
];

export default function HomeHero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const bgOpacity = useTransform(scrollYProgress, [0, 1], [0.28, 0.08]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80, duration: 1.4 });
      else el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section
      ref={ref}
      id="hero"
      data-testid="hero-section"
      className="relative pt-20 sm:pt-32 pb-10 sm:pb-28 overflow-hidden"
    >
      {/* Background layers with parallax */}
      <div className="absolute inset-0 bg-grid-faint pointer-events-none" />
      <div className="absolute inset-0 bg-radial-cyan pointer-events-none" />
      <div className="absolute inset-0 bg-radial-lime pointer-events-none" />
      <motion.div
        style={{ y: bgY, opacity: bgOpacity }}
        className="absolute inset-0 pointer-events-none mix-blend-screen"
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${HERO_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center right",
            maskImage:
              "radial-gradient(ellipse at center, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0) 75%)",
          }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-grain pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 sm:px-10">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 rounded-full border border-[#00E5FF]/25 bg-[#00E5FF]/5 px-3.5 py-1.5 text-[11px] tracking-[0.22em] uppercase font-semibold text-[#00E5FF]"
          data-testid="hero-eyebrow"
        >
          <Sparkles size={14} />
          For Growing Businesses
        </motion.div>

        {/* Signature masked line-by-line reveal */}
        <h1
          data-testid="hero-headline"
          className="font-display mt-4 text-[30px] leading-[0.96] sm:text-[68px] lg:text-[72px] font-semibold tracking-[-0.03em] max-w-5xl"
        >
          <span className="reveal-line">
            <span style={{ animationDelay: "0.1s" }}>
              <span className="text-white">AI-Powered</span>{" "}
              <span className="font-serif italic text-white/95">Operations</span>.
            </span>
          </span>
          <span className="reveal-line">
            <span style={{ animationDelay: "0.28s" }}>
              <span className="text-gradient-cyan">Human-Led</span>{" "}
              <span className="text-white">Delivery.</span>
            </span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.55 }}
          className="mt-8 text-base sm:text-lg leading-relaxed text-slate-400 max-w-2xl"
        >
          A4 Assist combines AI-enabled workflows with trained specialists to
          run your recruiting, your operations, and your data work — so you
          get the output of a bigger team without building one.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="mt-10 flex flex-col sm:flex-row gap-3 sm:items-center"
        >
          <a
            href={calendlyUrl()}
            {...calendlyLinkProps}
            data-testid="hero-primary-cta"
            className="group inline-flex items-center justify-center gap-2 bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-[#050B14] font-semibold px-7 py-4 rounded-md transition-all shadow-[0_0_30px_-8px_rgba(0,229,255,0.7)] hover:shadow-[0_0_40px_-6px_rgba(0,229,255,0.9)]"
          >
            Get My Free Operations Audit
            <ArrowRight size={18} className="arrow-shift" />
          </a>
          <button
            onClick={() => scrollTo("approach")}
            data-testid="hero-secondary-cta"
            className="group inline-flex items-center justify-center gap-2 border border-slate-700 hover:border-[#00E5FF]/60 text-white font-semibold px-7 py-4 rounded-md transition-colors bg-white/[0.02] hover:bg-[#00E5FF]/[0.04]"
          >
            <PlayCircle size={18} className="text-[#00E5FF]" /> See How It Works
          </button>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.9 }}
          className="mt-14 text-xs text-slate-500 max-w-xl"
          data-testid="hero-trust-line"
        >
          Trusted by businesses across{" "}
          <span className="text-slate-300">staffing, healthcare, real estate, insurance, and eCommerce</span>{" "}
          in the US, UK & EU.
        </motion.p>

        {/* Stat bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.05 }}
          className="mt-14 sm:mt-16 grid grid-cols-1 sm:grid-cols-3 gap-px bg-slate-800/60 border border-slate-800 rounded-xl overflow-hidden"
        >
          {stats.map((s, i) => (
            <div
              key={i}
              data-testid={`hero-stat-${i}`}
              className="bg-[#070F1F] px-7 py-7 flex flex-col gap-1 relative"
            >
              {!s.verified && (
                <span className="absolute top-3 right-3 text-[9px] tracking-[0.2em] uppercase font-bold text-slate-500 border border-slate-700 rounded-full px-2 py-0.5">
                  Placeholder
                </span>
              )}
              <div
                className={`font-display text-3xl sm:text-4xl font-bold tracking-tight ${
                  s.verified ? "text-white" : "text-slate-500"
                }`}
              >
                {s.value}
              </div>
              <div className="text-sm text-slate-400 leading-snug">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
